import { MediaContent } from '@/types/exam';
import { MathRenderer } from './MathRenderer';
import { CodeBlock } from './CodeBlock';
import { AudioPlayer } from './AudioPlayer';
import { ListeningPlayer } from './ListeningPlayer';
import { ImageBlock } from './ImageBlock';
import { TextBlock } from './TextBlock';

/**
 * Un contenu « audio » est soit une URL de fichier (lecteur classique), soit un
 * SCRIPT textuel lu en synthèse vocale (exercice de listening, texte masqué).
 * Une vraie ressource commence par http(s):// ou / (ou a une extension audio) ;
 * sinon on considère que c'est un script à faire lire par le navigateur.
 */
function isAudioUrl(v: string): boolean {
  const s = v.trim();
  return /^https?:\/\//i.test(s) || s.startsWith('/') || /\.(mp3|wav|ogg|m4a|aac)(\?|$)/i.test(s);
}

interface RichContentProps {
  content: MediaContent[];
  className?: string;
  inline?: boolean;
}

export function RichContent({ content, className = '', inline = false }: RichContentProps) {
  if (content.length === 0) return null;

  if (inline && content.length === 1) {
    const item = content[0];
    switch (item.type) {
      case 'text':
        return <TextBlock text={item.content} inline className={className} />;
      case 'math':
        return <MathRenderer latex={item.content} displayMode={item.displayMode} className={className} />;
      case 'code':
        return <code className={`text-xs bg-overlay-2 px-1.5 py-0.5 rounded font-mono text-green-300 ${className}`}>{item.content}</code>;
      default:
        return <TextBlock text={item.content} inline className={className} />;
    }
  }

  return (
    <div className={`space-y-2 ${className}`}>
      {content.map((item, index) => {
        switch (item.type) {
          case 'text':
            return <TextBlock key={index} text={item.content} />;
          case 'math':
            return <MathRenderer key={index} latex={item.content} displayMode={item.displayMode ?? true} />;
          case 'code':
            return <CodeBlock key={index} code={item.content} language={item.language} />;
          case 'audio':
            return isAudioUrl(item.content)
              ? <AudioPlayer key={index} src={item.content} />
              : <ListeningPlayer key={index} text={item.content} lang={item.language || 'en-US'} />;
          case 'image':
            return <ImageBlock key={index} src={item.content} alt={item.alt} />;
          default:
            return null;
        }
      })}
    </div>
  );
}
